import React from "react";
import { getFontSize } from "../utils/dimensions";
import { IScreenDimensions } from "../theme/sizes";
import { useScreenSizes } from "./useSizes";
import { useTheme } from "./useTheme";

const LINE_HEIGHT_MULTIPLIER = 1.5;

type UseLineHeightProps = {
  variant: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span" | "p";
  size?: "default" | "small" | "large" | "tiny" | "extra_small" | number;
  lineHeight?: number;
};

const useLineHeight = ({ variant, size, lineHeight }: UseLineHeightProps) => {
  const theme = useTheme();
  const screenSizes = useScreenSizes();

  return React.useMemo(() => {
    let curSize = theme.fonts.semantics.span.sizes.default;
    let curHeight = theme.fonts.semantics.span.heights.default;

    switch (variant) {
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
        curSize = getFontSize({
          sizes: screenSizes,
          dimensions: theme.fonts.semantics[variant].sizes,
        });
        curHeight = getFontSize({
          sizes: screenSizes,
          dimensions: theme.fonts.semantics[variant].heights,
        });
        break;
      case "span":
        curSize = theme.fonts.semantics.span.sizes[size] as number;
        curHeight = theme.fonts.semantics.span.heights[size] as number;
        break;
      case "p":
      default:
        if (size === "large") {
          curSize = getFontSize({
            sizes: screenSizes,
            dimensions: theme.fonts.semantics.p.sizes
              .large as IScreenDimensions,
          });
          curHeight = getFontSize({
            sizes: screenSizes,
            dimensions: theme.fonts.semantics.p.heights
              .large as IScreenDimensions,
          });
        } else {
          curSize = theme.fonts.semantics.p.sizes[size] as number;
          curHeight = theme.fonts.semantics.p.heights[size] as number;
        }
    }

    if (typeof size === "number" && size) {
      curSize = size;
      curHeight = Math.round(size * LINE_HEIGHT_MULTIPLIER);
    }

    if (typeof lineHeight === "number" && lineHeight) {
      curHeight = lineHeight;
    }

    return { fontSize: curSize, lineHeight: curHeight };
  }, [theme, size, screenSizes, variant, lineHeight]);
};

export { useLineHeight };
